import { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, Checkbox, Input, NoticeBox, Tags } from "../../ui";
import { ConfirmModal } from "../../modals/Confirm";
import { assignMenuBranchAPI, unassignMenuBranchAPI } from "../../../utils/api/menu";
import { menuErrorMessage, useMenuRole } from "../menuRole";
import { useAccess } from "../../../access/hooks/useAccess";
import { menuActionsText } from "../menuActionsText";

// Menu <-> branch is many-to-many. The user ticks any number of branches, then
// saves once; each assignment/removal goes through the existing per-branch
// endpoint (validated, saved and recorded in the menu history). Only the
// restaurant's own branches are listed (the API returns nothing else).
export default function MenuBranchesPanel({ menu, otherMenus = [], onMenuChange }) {
  const { lang } = useContext(Context);
  const t = menuActionsText(lang);
  const role = useMenuRole();
  const { can } = useAccess();
  const branches = useMemo(() => menu.restaurant?.branches || [], [menu.restaurant]);
  const assigned = useMemo(() => new Set((menu.branches || []).map(String)), [menu.branches]);
  const [selected, setSelected] = useState(() => new Set(assigned));
  const [query, setQuery] = useState("");
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const canManage = role.canWrite && menu.lifecycle === "active";

  // Server state wins whenever the menu changes (after a save or a switch).
  useEffect(() => setSelected(new Set(assigned)), [assigned]);

  const id = (branch) => String(branch._id);
  const toAdd = branches.filter((b) => selected.has(id(b)) && !assigned.has(id(b)));
  const toRemove = branches.filter((b) => !selected.has(id(b)) && assigned.has(id(b)));
  const q = query.trim().toLowerCase();
  const shown = q ? branches.filter((b) => String(b.name || "").toLowerCase().includes(q)) : branches;
  const alsoUsing = (branchId) => otherMenus.filter((m) => String(m._id) !== String(menu._id) && m.lifecycle !== "archived" && (m.branches || []).map(String).includes(branchId)).map((m) => m.name);

  const toggle = (branchId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(branchId)) next.delete(branchId);
      else next.add(branchId);
      return next;
    });
  const setMany = (list, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      list.forEach((b) => (on ? next.add(id(b)) : next.delete(id(b))));
      return next;
    });

  const apply = async () => {
    // handler-level authorization, against this menu's restaurant
    if (!can("menu.manage", { restaurantId: menu.restaurant?.id })) return;
    setConfirmRemove(false);
    setResult(null);
    const ops = [...toAdd.map((branch) => ["add", branch]), ...toRemove.map((branch) => ["remove", branch])];
    let latest = null;
    const done = { add: 0, remove: 0 };
    const failed = [];
    for (const [index, [op, branch]] of ops.entries()) {
      setProgress({ done: index, total: ops.length });
      try {
        const res = op === "add" ? await assignMenuBranchAPI(menu._id, branch._id) : await unassignMenuBranchAPI(menu._id, branch._id);
        latest = res.menu;
        done[op] += 1;
      } catch (error) {
        failed.push(`${branch.name}: ${menuErrorMessage(error, lang)}`);
      }
    }
    setProgress(null);
    setResult({ added: done.add, removed: done.remove, failed });
    if (latest) onMenuChange?.(latest);
  };

  const status = (branch) => {
    const on = selected.has(id(branch));
    const was = assigned.has(id(branch));
    if (on && !was) return <Tags variant="primary" size="sm">{t.willAssign}</Tags>;
    if (!on && was) return <Tags variant="danger" size="sm">{t.willRemove}</Tags>;
    if (was) return <Tags variant="success" size="sm">{t.assigned}</Tags>;
    return null;
  };

  const pending = toAdd.length + toRemove.length;

  return (
    <section className="menu-branches-panel" aria-labelledby="menu-branches-title">
      <header>
        <h4 id="menu-branches-title">{t.branchesTitle}</h4>
        <p className="menu-note">{menu.lifecycle === "draft" ? t.branchesDraft : menu.lifecycle === "archived" ? t.branchesArchived : t.branchesHint}</p>
      </header>

      {branches.length === 0 ? (
        <p className="menu-empty-state">{t.noBranches}</p>
      ) : (
        <>
          {canManage && (
            <div className="menu-branches-tools">
              {branches.length > 6 && <Input type="search" placeholder={t.searchBranches} aria-label={t.searchBranches} value={query} onChange={(e) => setQuery(e.target.value)} fullWidth={false} />}
              <Button type="button" variant="primary" appearance="soft" size="xs" rounded disabled={Boolean(progress)} onClick={() => setMany(shown, true)}>
                {t.selectAll}
              </Button>
              <Button type="button" variant="neutral" appearance="soft" size="xs" rounded disabled={Boolean(progress)} onClick={() => setMany(shown, false)}>
                {t.clearAll}
              </Button>
            </div>
          )}
          {shown.length === 0 ? (
            <p className="menu-empty-state">{t.noBranchMatch}</p>
          ) : (
            <ul className="menu-branches-list">
              {shown.map((branch) => {
                const others = alsoUsing(id(branch));
                return (
                  <li key={branch._id}>
                    <div className="menu-branch-row">
                      <Checkbox label={branch.name || branch._id} checked={selected.has(id(branch))} disabled={!canManage || Boolean(progress)} onChange={() => toggle(id(branch))} />
                      {status(branch)}
                    </div>
                    {others.length > 0 && <small className="menu-secondary">{t.alsoUses(others.join(lang === "ar" ? "، " : ", "))}</small>}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {result && (
        <NoticeBox variant={result.failed.length ? "warning" : "success"} size="sm" title={t.branchesSaved(result.added, result.removed)}>
          {result.failed.length > 0 && (
            <>
              <p>{t.partialFailure(result.failed.length)}</p>
              <ul>
                {result.failed.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </>
          )}
        </NoticeBox>
      )}

      {canManage && branches.length > 0 && (
        <div className="menu-branches-actions">
          <Button type="button" variant="primary" appearance="filled" size="sm" rounded disabled={!pending || Boolean(progress)} loading={Boolean(progress)} onClick={() => (toRemove.length ? setConfirmRemove(true) : apply())}>
            {pending ? t.applyChanges(toAdd.length, toRemove.length) : t.noChanges}
          </Button>
          {pending > 0 && !progress && (
            <Button type="button" variant="neutral" appearance="soft" size="sm" rounded onClick={() => setSelected(new Set(assigned))}>
              {t.cancel}
            </Button>
          )}
          {progress && <span className="menu-secondary" role="status">{t.progress(progress.done, progress.total)}</span>}
        </div>
      )}

      {confirmRemove && <ConfirmModal tone="danger" title={t.confirmRemoveBranches(toRemove.length)} message={`${toRemove.map((b) => b.name).join(lang === "ar" ? "، " : ", ")}\n${t.confirmRemoveBranchesBody}`} confirmText={t.save} cancelText={t.cancel} onConfirm={apply} onCancel={() => setConfirmRemove(false)} />}
    </section>
  );
}
